// Image magnify functionality
export class ImageMagnify {
  constructor(options = {}) {
    this.overlay = null;
    this.zoomRatio = options.zoomRatio || 2;
    this.selectors = options.selectors || document.querySelectorAll('.image-magnify-hover');
    this.init();
  }

  init() {
    this.enableZoomOnHover();
  }

  // Create a container and set the full-size image as its background
  createOverlay(image) {
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${image.src}`);
    this.overlay = document.createElement('div');
    this.prepareOverlay(this.overlay, overlayImage);

    this.overlay.classList.add('loading');
    
    const spinnerTimeout = setTimeout(() => {
      this.createLoadingSpinner(image.parentElement);
    }, 200);

    overlayImage.onload = () => {
      clearTimeout(spinnerTimeout); // Clear timeout if image loads before spinner shows
      this.overlay.classList.remove('loading');
      this.removeLoadingSpinner(image.parentElement);
      image.parentElement.insertBefore(this.overlay, image);
    };

    return this.overlay;
  }

  prepareOverlay(container, image) {
    container.setAttribute('class', 'image-magnify-full-size');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
    container.style.backgroundColor = 'var(--gradient-background)';
  }

  createLoadingSpinner(container) {
    let loadingSpinner = container.querySelector('.image-magnify-spinner');
    
    if (!loadingSpinner) {
      // Create spinner if it doesn't exist
      loadingSpinner = document.createElement('div');
      loadingSpinner.className = 'image-magnify-spinner';
      loadingSpinner.innerHTML = `
        <div class="bouncing-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      `;
      container.appendChild(loadingSpinner);
    }
  }

  toggleLoadingSpinner(container) {
    let loadingSpinner = container.querySelector('.image-magnify-spinner');
    
    if (!loadingSpinner) {
      // Create spinner if it doesn't exist
      loadingSpinner = document.createElement('div');
      loadingSpinner.className = 'image-magnify-spinner';
      loadingSpinner.innerHTML = `
        <svg class="spinner-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="31.416" stroke-dashoffset="31.416">
            <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
            <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
          </circle>
        </svg>
      `;
      container.appendChild(loadingSpinner);
    }
    
    loadingSpinner.classList.toggle('hidden');
  }

  removeLoadingSpinner(container) {
    const loadingSpinner = container.querySelector('.image-magnify-spinner');
    if (loadingSpinner) {
      loadingSpinner.remove();
    }
  }

  moveWithHover(image, event, zoomRatio) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    this.overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    this.overlay.style.backgroundSize = `${image.width * zoomRatio}px`;
  }

  magnify(image, zoomRatio) {
    const overlay = this.createOverlay(image);
    overlay.onclick = () => overlay.remove();
    overlay.onmousemove = (event) => this.moveWithHover(image, event, zoomRatio);
    overlay.onmouseleave = () => {
      overlay.remove();
      this.removeLoadingSpinner(overlay);
    };
  }

  enableZoomOnHover(zoomRatio = null) {
    const images = this.selectors;
    const finalZoomRatio = zoomRatio || this.zoomRatio;
    images.forEach((image) => {
      image.onclick = (event) => {
        this.magnify(image, finalZoomRatio);
        this.moveWithHover(image, event, finalZoomRatio);
      };
    });
  }

  destroy() {
    // Remove onclick handlers from images and cleanup DOM elements
    if (this.selectors) {
      this.selectors.forEach((image) => {
        // Remove onclick handler
        image.onclick = null;
        
        // Remove overlay and spinner elements from each image's container
        if (image.parentElement) {
          const container = image.parentElement;
          const overlays = container.querySelectorAll('.image-magnify-full-size');
          overlays.forEach(overlay => overlay.remove());
          
          const spinners = container.querySelectorAll('.image-magnify-spinner');
          spinners.forEach(spinner => spinner.remove());
        }
      });
    }
    
    // Remove overlay stored in instance (if any)
    if (this.overlay && this.overlay.parentElement) {
      this.overlay.remove();
    }
    
    // Clear references
    this.overlay = null;
  }
}

// Add to window.LuTheme for backward compatibility
if (typeof window !== 'undefined') {
  if (!window.LuTheme) {
    window.LuTheme = {};
  }
  window.LuTheme.ImageMagnify = ImageMagnify;
}
